import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Brain, Target } from 'lucide-react';
import EdpiesLogo from '@/components/EdpiesLogo';

const Onboarding = ({ setProgress }) => {
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const navigate = useNavigate();

  const handleStart = () => {
    if (selectedOption === 'diagnostic') {
      setProgress({
        competency_level: null,
        diagnostic_completed: false,
        stage1_passed: false,
        completed_tests: []
      });
      navigate('/ks2-assessment');
    } else if (selectedLevel) {
      setProgress({
        competency_level: selectedLevel,
        diagnostic_completed: false,
        stage1_passed: false,
        completed_tests: []
      });
      navigate('/11plus/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Card className="w-full max-w-2xl shadow-2xl animate-fade-in" data-testid="onboarding-card">
        <CardHeader className="text-center space-y-2 pb-6">
          <div className="flex justify-center mb-4">
            <EdpiesLogo size="xl" />
          </div>
          <CardTitle className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
            Welcome to Edpies
          </CardTitle>
          <CardDescription className="text-base text-gray-600">
            Prepare for Sutton SET exams with comprehensive mock papers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              Choose Your Starting Point
            </h3>
            <RadioGroup value={selectedOption} onValueChange={setSelectedOption}>
              <div className="flex items-start space-x-3 p-4 rounded-lg border-2 border-transparent hover:border-purple-300 hover:bg-purple-50 transition-all cursor-pointer" data-testid="option-diagnostic">
                <RadioGroupItem value="diagnostic" id="diagnostic" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="diagnostic" className="text-base font-medium cursor-pointer">
                    Take Adaptive Assessment
                  </Label>
                  <p className="text-sm text-gray-600 mt-1">
                    A quick 10-question assessment to gauge your current level in Maths and English
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-4 rounded-lg border-2 border-transparent hover:border-purple-300 hover:bg-purple-50 transition-all cursor-pointer" data-testid="option-manual">
                <RadioGroupItem value="manual" id="manual" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="manual" className="text-base font-medium cursor-pointer">
                    Select Competency Level
                  </Label>
                  <p className="text-sm text-gray-600 mt-1">
                    Choose your current skill level and start practicing immediately
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {selectedOption === 'manual' && (
            <div className="space-y-4 animate-slide-in">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-600" />
                Select Your Level
              </h3>
              <RadioGroup value={selectedLevel} onValueChange={setSelectedLevel}>
                <div className="flex items-center space-x-3 p-4 rounded-lg border-2 border-transparent hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer" data-testid="level-beginner">
                  <RadioGroupItem value="beginner" id="beginner" />
                  <Label htmlFor="beginner" className="text-base font-medium cursor-pointer flex-1">
                    Beginner - Just starting 11+ preparation
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 rounded-lg border-2 border-transparent hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer" data-testid="level-intermediate">
                  <RadioGroupItem value="intermediate" id="intermediate" />
                  <Label htmlFor="intermediate" className="text-base font-medium cursor-pointer flex-1">
                    Intermediate - Some practice done
                  </Label>
                </div>
                <div className="flex items-center space-x-3 p-4 rounded-lg border-2 border-transparent hover:border-indigo-300 hover:bg-indigo-50 transition-all cursor-pointer" data-testid="level-advanced">
                  <RadioGroupItem value="advanced" id="advanced" />
                  <Label htmlFor="advanced" className="text-base font-medium cursor-pointer flex-1">
                    Advanced - Confident and exam-ready
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          <Button
            onClick={handleStart}
            disabled={!selectedOption || (selectedOption === 'manual' && !selectedLevel)}
            className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg"
            data-testid="start-button"
          >
            Start Your Journey
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;
